import { useRef } from "react"

import type { UseQueryResult } from "@tanstack/react-query"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateBatteryProposalForArbitrage } from "./battery-arbitrage-cache"
import type { BatteryArbitrageDto } from "../dto/battery-arbitrage.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

/**
 * How often to re-read while the gateway still owes an answer. The queue scans
 * pending Proposals every five seconds after a two-second quiet period, so a
 * three-second poll sees a completed run about as soon as one exists without
 * hammering the read.
 */
export const ARBITRAGE_POLL_MS = 3_000

/**
 * Statuses that mean a calculation is already scheduled or running. Only these
 * poll: every other status is settled until the seller or a committed input
 * change moves it, and polling one would spin forever.
 */
const IN_FLIGHT: ReadonlySet<string> = new Set([`pending`, `running`])

export const savingsAreShown = (
  visibility: BatteryArbitrageDto[`visibility`] | undefined,
): boolean => (visibility ?? `shown`) === `shown`

export const isBatteryArbitrageInFlight = (
  arbitrage: BatteryArbitrageDto | undefined,
): boolean =>
  arbitrage != null &&
  savingsAreShown(arbitrage.visibility) &&
  IN_FLIGHT.has(arbitrage.status)

/**
 * Reads settings, lifecycle status, and the current saved result. The GET never
 * starts work, so mounting this drawer cannot trigger a calculation; automatic
 * scheduling stays entirely on the gateway.
 */
export default function useBatteryProposalArbitrage(
  proposalId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<BatteryArbitrageDto> {
  const queryClient = useQueryClient()
  const lastStatus = useRef<string | null>(null)

  return useQuery({
    queryKey: queryKeys.batteryProposal.arbitrage(proposalId ?? ``),
    queryFn: () => {
      if (!proposalId) {
        throw new Error(`Battery-Only Proposal id is required`)
      }

      return batteryProposalService.getArbitrage(proposalId)
    },
    enabled: Boolean(proposalId) && enabled,
    retry: 1,
    // The background queue, not this client, is what moves this row. Under the
    // app's five-minute default staleTime a reopened drawer would show the
    // status as it stood when the drawer last closed.
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: (data) =>
      isBatteryArbitrageInFlight(data) ? ARBITRAGE_POLL_MS : false,
    onSuccess: (arbitrage) => {
      // The Proposal read carries the copy of this result that the seller
      // presentation renders. Nothing else tells it the lifecycle moved, so a
      // run that completes — or goes outdated — under a page that has been open
      // past the app's default staleTime would leave the figures behind this
      // drawer disagreeing with the status in it.
      if (lastStatus.current === arbitrage.status) {
        return
      }
      const first = lastStatus.current === null
      lastStatus.current = arbitrage.status
      if (!first) {
        invalidateBatteryProposalForArbitrage(queryClient, proposalId ?? ``)
      }
    },
  })
}
